// Extend Chakra to include Finley colors, fonts, etc
export const colors = {
  // core colors: main UI colors
  white: '#FFFFFF',
  black: {
    10: '#F6F6F6',
    20: '#ECECEC',
    30: '#E3E3E3',
    40: '#DADADA',
    50: '#C7C7C7',
    60: '#B5B5B5',
    70: '#737373',
    80: '#40413E',
    95: '#0E0E0E',
  },
  error: 'red.400',
  success: 'green.400',
  warning: 'orange.400',
  info: 'blue.400',
  background: {
    50: '#FDFCF9',
    100: '#FAF7F3',
    200: '#F7F4EE',
    300: '#F4F1EA',
    400: '#F9F6F1',
    500: '#E7E3DE',
    600: '#E4E0DA',
    700: '#DCD9D4',
    800: '#D4D0CB',
    900: '#C1BDB8',
  },
  tableHeader: '#FAFAFA',
  red: {
    100: 'rgba(255, 115, 115, 0.1)',
    200: '#FF7373',
    300: '#F55656',
    400: '#DB3737',
    500: '#C23030',
    600: '#A82A2A',
  },
  orange: {
    75: '#F7E0B4',
    100: 'rgba(255, 179, 102, 0.1)',
    200: '#FFB366',
    300: '#F29D49',
    400: '#D9822B',
    500: '#BF7326',
    600: '#A66321',
  },
  green: {
    100: 'rgba(61, 204, 145, 0.1)',
    200: '#3DCC91',
    300: '#15B371',
    400: '#0F9960',
    500: '#0D8050',
    600: '#0A6640',
  },
  blue: {
    100: 'rgba(72, 175, 240, 0.1)',
    200: '#48AFF0',
    300: '#2B95D6',
    400: '#137CBD',
    500: '#106BA3',
    600: '#0E5A8A',
  },
  gray: {
    20: '#F7F9F9',
    50: '#F5F8FA',
    100: '#EBF1F5',
    200: '#E1E8ED',
    300: '#CED9E0',
    400: '#A7B6C2',
    500: '#8A9BA8',
    600: '#5C7080',
    700: '#394B59',
    800: '#293742',
    900: '#182026',
  },
  // brand colors
  poppy: {
    50: '#FDF7ED',
    300: '#F4C671', // brand guide Poppy 30
    400: '#EBB347', // brand guide Poppy 40
    500: '#D99C26', // brand guide Poppy 50
  },
  sage: {
    50: '#F8FAF6', // brand guide Sage 02
    100: '#DFE4D5', // brand guide Sage 10
  },
  aurora: {
    50: '#E6ECED',
    75: '#97B2B5',
    100: '#4D8F99',
    400: '#012E32',
    500: '#012E32',
    800: '#02444A', // brand guide Aurora 75
    900: '#012E32', // brand guide Aurora 90
  },
  millet: {
    50: '#FBFAF9', // brand guide Millet 02
    100: '#F9F6F1', // brand guide Millet 04
    200: '#F3F0ED', // brand guide Millet 06
  },
  // extended colors: brand colors, background colors, or colors reserved for data visualizations
  vermillion: {
    100: '#FFB7A5',
    200: '#F4A691',
    300: '#E9947D',
    400: '#DD836A',
    500: '#D17257',
    600: '#C56145',
    700: '#B85033',
    800: '#AB3E21',
    900: '#9E2B0E',
  },
  rose: {
    100: '#FFB3D0',
    200: '#F5A2C0',
    300: '#EB91AF',
    400: '#E080A0',
    500: '#D56F90',
    600: '#CA5D81',
    700: '#BF4B72',
    800: '#B43863',
    900: '#A82255',
  },
  brick: {
    50: '#FEF8F8',
    100: '#FDF0F0',
    150: '#FDE9E9',
    200: '#FCE1E1',
    300: '#FAD2D2',
    400: '#F9C3C3',
    500: '#FF9B9B',
    600: '#E57373',
    700: '#C25B5B',
    800: '#A54242',
    850: '#9D3F3F',
    900: '#912E2E',
    950: '#863030',
  },
  violet: {
    100: '#E1BAE1',
    200: '#D0A6CF',
    300: '#BF93BE',
    400: '#AE80AD',
    500: '#9D6D9C',
    600: '#8C5B8C',
    700: '#7C497B',
    800: '#6C376B',
    900: '#5C255C',
  },
  indigo: {
    100: '#D6CCFF',
    200: '#C6BAF4',
    300: '#B7A8E8',
    400: '#A796DD',
    500: '#9784D2',
    600: '#8773C7',
    700: '#7763BC',
    800: '#6752B1',
    900: '#5642A6',
  },
  cobalt: {
    100: '#B3CFFF',
    200: '#A2BDF2',
    300: '#91ACE5',
    400: '#809BD8',
    500: '#6F8ACB',
    600: '#5E79BE',
    700: '#4B6AB2',
    800: '#385AA5',
    900: '#1F4B99',
  },
  turquoise: {
    100: '#87E4DB',
    200: '#78D5CC',
    300: '#68C6BD',
    400: '#58B8AE',
    500: '#47AA9F',
    600: '#369C91',
    700: '#369C91',
    800: '#228E83',
    900: '#008075',
  },
  forest: {
    100: '#B1ECB5',
    200: '#9FDCA2',
    300: '#8DCD8F',
    400: '#7CBD7D',
    500: '#6AAE6A',
    600: '#589F59',
    700: '#469047',
    800: '#338136',
    900: '#1D7324',
  },
  lime: {
    100: '#E8F9B6',
    200: '#D9EBA3',
    300: '#CADD91',
    400: '#BBCF7F',
    500: '#ADC16D',
    600: '#9EB45B',
    700: '#8FA649',
    800: '#819937',
    900: '#728C23',
  },
  gold: {
    100: '#FFE4A0',
    200: '#F4D68E',
    300: '#EAC87D',
    400: '#DFBA6B',
    500: '#D4AD5A',
    600: '#C8A048',
    700: '#BD9336',
    800: '#B28623',
    900: '#A67908',
  },
  sepia: {
    100: '#E4CBB2',
    200: '#D4B89E',
    300: '#C3A68A',
    400: '#B39477',
    500: '#A38364',
    600: '#937252',
    700: '#836140',
    800: '#73512F',
    900: '#63411E',
  },
  chart: {
    celadon: '#B4F7BA',
    mint: '#B4F7D5',
    lilac: '#BFB4F7',
    cream: '#F7ECB4',
    blush: '#F7D0B4',
    sky: '#B4E1F7',
    teal: '#B4F7F1',
    jasmine: '#CAF7B4',
    // Judy's v2 colors, as of 2023-10-20
    // Todo: update all charts to v2 colors at some point
    // Mapping on figma: https://www.figma.com/file/Bb83U2hoFkfEiEpqGxptVf/Lender-Dashboard?node-id=3366%3A50266&mode=dev
    paleViolet: '#D2A0FF',
    winter: '#A0E6FF',
    pink: '#FFA0DC',
  },
};

/**
 * There is now general browser compatibility for 8-digit hex codes
 * where the first 6 digits denote the regular hex color
 * and the last 2 digits denote transparency.
 * Use the below in combination with colors as needed.
 *
 * Eg.`${colors.sepia[500]}${transparency[50]}`
 */
export const transparency = {
  100: 'FF',
  95: 'F2',
  90: 'E6',
  85: 'D9',
  80: 'CC',
  75: 'BF',
  70: 'B3',
  65: 'A6',
  60: '99',
  55: '8C',
  50: '80',
  45: '73',
  40: '66',
  35: '59',
  30: '4D',
  25: '40',
  20: '33',
  15: '26',
  10: '1A',
  5: '0D',
  0: '00',
};
